import { useEffect, useState } from "react";
import Modal from "../common/Modal";
import EmailChipInput from "./EmailChipInput";
import { createRecipientList, updateRecipientList } from "../../firebase/recipientLists";

export default function RecipientListModal({ open, onClose, uid, existing, onSaved }) {
  const [name, setName] = useState("");
  const [recipients, setRecipients] = useState([]);
  const [nameError, setNameError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    if (open) {
      setName(existing?.name || "");
      setRecipients(existing?.recipients || []);
      setNameError(null);
      setSaveError(null);
    }
  }, [open, existing]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (saving) return;

    const trimmedName = name.trim();

    if (!trimmedName) {
      setNameError("Give this list a name, like \u201cSales Team\u201d.");
      return;
    }

    if (recipients.length === 0) {
      setSaveError("Add at least one recipient.");
      return;
    }

    setSaving(true);
    setSaveError(null);

    try {
      if (existing?.id) {
        await updateRecipientList(existing.id, { name: trimmedName, recipients });
      } else {
        await createRecipientList(uid, { name: trimmedName, recipients });
      }
      onSaved?.();
      onClose();
    } catch (err) {
      console.error("Failed to save recipient list:", err);
      setSaveError("We couldn't save this list. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} dismissible={!saving} title={existing?.id ? "Edit Recipient List" : "New Recipient List"}>
      <form onSubmit={handleSave} className="space-y-4" noValidate>
        <div>
          <label htmlFor="list-name" className="block text-xs font-medium text-gray-400 mb-1.5">List name</label>
          <input
            id="list-name"
            type="text"
            value={name}
            onChange={(e) => { setName(e.target.value); setNameError(null); }}
            placeholder="Sales Team"
            disabled={saving}
            className="w-full bg-gray-950 border border-gray-700 focus:border-blue-500 rounded-lg px-3 py-2 text-sm text-white outline-none disabled:opacity-60"
          />
          {nameError && <p className="mt-1 text-xs text-red-400">{nameError}</p>}
        </div>

        <div>
          <label htmlFor="list-recipients" className="block text-xs font-medium text-gray-400 mb-1.5">
            Recipients ({recipients.length})
          </label>
          <EmailChipInput id="list-recipients" emails={recipients} onChange={setRecipients} disabled={saving} />
        </div>

        {saveError && <p className="text-sm text-red-400">{saveError}</p>}

        <div className="flex justify-end gap-2.5 pt-1">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2.5 rounded-xl bg-gray-800 hover:bg-gray-700 border border-gray-700 transition text-sm font-medium disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 transition text-sm font-semibold disabled:opacity-60 flex items-center gap-2"
          >
            {saving && <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />}
            {saving ? "Saving..." : existing?.id ? "Save Changes" : "Create List"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
